import { DateTime as DT } from 'luxon';
import type {
    LeadType,
    ObjectType,
    TaskCreateDataType,
    TaskCreateFormType,
    TaskForType,
    UserType,
} from '@econtractor/types';

export type { TaskCreateFormType };

const OBJECT_BOUND_TASK_FOR: TaskForType[] = ['object', 'sale', 'service'];

const isFlagOn = (value?: boolean | number | null): boolean =>
    Number(value) === 1 || value === true;

export const isObjectBoundTaskFor = (taskFor: TaskForType): boolean =>
    OBJECT_BOUND_TASK_FOR.includes(taskFor);

export const resolveTaskForFromObject = (object?: ObjectType | null): TaskForType => {
    if (isFlagOn(object?.onlyService)) return 'service';
    if (isFlagOn(object?.onlySale)) return 'sale';
    return 'object';
};

export const resolveTaskFor = (
    leadId: number | null = null,
    objectId: number | null = null,
    object: ObjectType | null = null,
): TaskForType => {
    if (objectId) return resolveTaskForFromObject(object);
    if (leadId) return 'lead';
    return 'personal';
};

/** Початковий термін у формі створення (наприклад з кліку в календарі) */
export type NewTaskDefaultDue = {
    dateMillis: number;
    allDay: boolean;
};

const resolveDefaultDueDate = (defaultDue?: NewTaskDefaultDue | null): DT => {
    const now = DT.now().setZone('local');
    const ms = Number(defaultDue?.dateMillis || 0);
    if (!(ms > 0)) return now;
    const parsed = DT.fromMillis(ms, { zone: 'local' });
    return parsed.isValid ? parsed : now;
};

export const getInitNewTaskStruct = (
    leadId: number | null = null,
    objectId: number | null = null,
    currentUser: UserType | null = null,
    lead: LeadType | null = null,
    object: ObjectType | null = null,
    defaultDue: NewTaskDefaultDue | null = null,
): TaskCreateFormType => {
    const due = resolveDefaultDueDate(defaultDue);
    const withTime = Boolean(defaultDue) && defaultDue?.allDay === false;
    const taskFor = resolveTaskFor(leadId, objectId, object);
    return {
        taskFor,
        lead: taskFor === 'lead' ? lead : null,
        object: isObjectBoundTaskFor(taskFor) ? object : null,
        taskTypeId: null,
        taskName: '',
        taskInfo: '',
        executor: null,
        controller: currentUser,
        multiDay: false,
        withTime,
        dateStart: due.startOf('day').toMillis(),
        dateEnd: withTime ? due.toMillis() : due.endOf('day').toMillis(),
        startTime: '00:00',
        endTime: withTime ? due.toFormat('HH:mm') : '18:00',
    };
};

const applyClockToDay = (
    day: DT,
    time: string,
    fallbackHour: number,
): DT => {
    const [hh, mm] = String(time || '').split(':').map((v) => Number(v));
    const withClock = day.set({
        hour: Number.isFinite(hh) ? hh : fallbackHour,
        minute: Number.isFinite(mm) ? mm : 0,
        second: 0,
        millisecond: 0,
    });
    return withClock.isValid ? withClock : day;
};

/** Початок доби або дата+час (HH:mm) для date_start */
export const isoDateToStartMillis = (
    isoDate: string,
    withTime = false,
    time = '00:00',
): number | null => {
    const raw = String(isoDate || '').trim();
    if (!raw) return null;
    const day = DT.fromISO(raw, { zone: 'local' });
    if (!day.isValid) return null;
    if (!withTime) return day.startOf('day').toMillis();
    return applyClockToDay(day, time, 0).toMillis();
};

/** Кінець доби або дата+час (HH:mm) для date_end */
export const isoDateToEndMillis = (
    isoDate: string,
    withTime = false,
    time = '18:00',
): number | null => {
    const raw = String(isoDate || '').trim();
    if (!raw) return null;
    const day = DT.fromISO(raw, { zone: 'local' });
    if (!day.isValid) return null;
    // без галочки «Вказати час» — кінець доби
    if (!withTime) return day.endOf('day').toMillis();
    return applyClockToDay(day, time || '18:00', 18).toMillis();
};

/**
 * Перерахунок dateEnd з millis дати + withTime + endTime.
 * Бізнес-значення дедлайну — саме dateEnd; endTime лише джерело годин:хвилин при записі.
 */
export const resolveDateEndMillis = (
    dateEndMillis: number,
    withTime: boolean,
    endTime: string | null | undefined,
): number => {
    const endDateIso = millisToIsoDate(dateEndMillis);
    return (
        isoDateToEndMillis(endDateIso, withTime, endTime || '18:00') ||
        dateEndMillis
    );
};

export const millisToIsoDate = (millis: number | null | undefined): string => {
    const value = Number(millis || 0);
    if (!Number.isFinite(value) || value <= 0) return '';
    return DT.fromMillis(value, { zone: 'local' }).toISODate() || '';
};

export const millisToTime = (
    millis: number | null | undefined,
    fallback = '18:00',
): string => {
    const value = Number(millis || 0);
    if (!Number.isFinite(value) || value <= 0) return fallback;
    const dt = DT.fromMillis(value, { zone: 'local' });
    return dt.isValid ? dt.toFormat('HH:mm') : fallback;
};

/**
 * dateStart: без часу — початок доби; з часом — startTime цієї доби.
 * Для одноденної задачі день береться з dateEnd.
 */
export const resolveDateStartMillis = (
    dateStartMillis: number,
    dateEndMillis: number,
    multiDay: boolean,
    withTime: boolean,
    startTime: string | null | undefined,
): number => {
    const dayIso = millisToIsoDate(multiDay ? dateStartMillis : dateEndMillis);
    return (
        isoDateToStartMillis(dayIso, withTime, startTime || '00:00') ||
        dateStartMillis
    );
};

/**
 * Нормалізація перед сабмітом.
 * dateEnd — авторитетний дедлайн (кінець доби або дата+час).
 * endTime — лише інформаційне поле для БД (HH:mm), у порівняннях/термінах не використовується.
 */
export const normalizeNewTaskValues = (values: TaskCreateFormType): TaskCreateDataType => {
    const dateEnd = resolveDateEndMillis(values.dateEnd, values.withTime, values.endTime);
    const dateStart = resolveDateStartMillis(
        values.dateStart,
        dateEnd,
        values.multiDay,
        values.withTime,
        values.startTime,
    );

    const leadId =
        values.taskFor === 'lead' ? (values.lead?.ID ? Number(values.lead.ID) : null) : null;
    const objectId =
        isObjectBoundTaskFor(values.taskFor) && values.object?.ID
            ? Number(values.object.ID)
            : null;

    return {
        leadId,
        objectId,
        taskTypeId: values.taskTypeId,
        taskName: values.taskName,
        taskInfo: values.taskInfo,
        executorId: values.executor?.ID ?? null,
        controllerId: values.controller?.ID ?? null,
        multiDay: values.multiDay,
        withTime: values.withTime,
        dateStart,
        dateEnd,
        endTime: values.withTime ? String(values.endTime || '18:00').trim() || null : null,
    };
};
